<template>
  <el-dialog
    title="参数设置"
    :visible.sync="visibleProxy"
    width="880px"
    class="device-settings-dialog"
  >
    <div class="settings-layout">
      <nav class="settings-tabs">
        <button
          v-for="tab in tabs"
          :key="tab.value"
          type="button"
          :class="{ active: activeTab === tab.value }"
          @click="activeTab = tab.value"
        >
          {{ tab.label }}
        </button>
      </nav>

      <section class="settings-content">
        <el-form
          v-if="activeTab === 'system'"
          :model="systemForm"
          label-position="right"
          label-width="188px"
          class="settings-form settings-form--grid"
        >
          <el-form-item
            v-for="field in systemFields"
            :key="field.key"
            :label="`${field.label}：`"
          >
            <el-input-number
              v-model="systemForm[field.key]"
              :disabled="!editable"
              :precision="field.precision || 0"
              :min="0"
              :max="field.max || 255"
              :controls="false"
              class="settings-input"
            />
          </el-form-item>
          <div v-if="editable" class="settings-actions settings-actions--full">
            <el-button type="primary" @click="saveSystem">保存</el-button>
          </div>
        </el-form>

        <div v-else-if="activeTab === 'timing'" class="timing-settings">
          <div class="settings-tip">
            <span class="settings-tip__icon"></span>
            <p>设置说明：24时制（时:分），开始或停止设置为00:00无效，开始和停止之间跨越00:00也无效。</p>
          </div>
          <el-form
            :model="timingForm"
            label-position="right"
            label-width="204px"
            class="timing-days"
          >
            <el-form-item label="设置间隔天数，每...天执行一次：">
              <el-input-number
                v-model="timingForm.setIntervalDays"
                :disabled="!editable"
                :min="0"
                :max="255"
                :controls="false"
              />
            </el-form-item>
            <el-form-item label="已累计间隔天数：">
              <el-input-number
                v-model="timingForm.accumulatedDays"
                :disabled="!editable"
                :min="0"
                :max="255"
                :controls="false"
              />
            </el-form-item>
          </el-form>
          <div v-for="period in periods" :key="period" class="time-period">
            <h4>· 时间段{{ period === 1 ? "一" : period === 2 ? "二" : "三" }}</h4>
            <div v-for="device in timingDevices" :key="device.key" class="time-row">
              <strong>{{ device.label }}</strong>
              <span>开始</span>
              <el-input-number
                :value="getTimingValue(device.key, 'Start', 'Hour', period)"
                :disabled="!editable"
                :min="0"
                :max="23"
                :controls="false"
                @input="setTimingValue(device.key, 'Start', 'Hour', period, $event)"
              />
              <i>:</i>
              <el-input-number
                :value="getTimingValue(device.key, 'Start', 'Minute', period)"
                :disabled="!editable"
                :min="0"
                :max="59"
                :controls="false"
                @input="setTimingValue(device.key, 'Start', 'Minute', period, $event)"
              />
              <span>结束</span>
              <el-input-number
                :value="getTimingValue(device.key, 'End', 'Hour', period)"
                :disabled="!editable"
                :min="0"
                :max="23"
                :controls="false"
                @input="setTimingValue(device.key, 'End', 'Hour', period, $event)"
              />
              <i>:</i>
              <el-input-number
                :value="getTimingValue(device.key, 'End', 'Minute', period)"
                :disabled="!editable"
                :min="0"
                :max="59"
                :controls="false"
                @input="setTimingValue(device.key, 'End', 'Minute', period, $event)"
              />
            </div>
          </div>
          <div v-if="editable" class="settings-actions">
            <el-button type="primary" @click="saveTiming">保存</el-button>
          </div>
        </div>

        <el-form
          v-else-if="activeTab === 'clock'"
          :model="clockForm"
          label-position="right"
          label-width="118px"
          class="settings-form settings-form--clock"
        >
          <div class="settings-tip">
            <span class="settings-tip__icon"></span>
            <p>设置说明：因数据每30分钟更新一次，所以显示的主机时间可能是30分钟前的时间。</p>
          </div>
          <p class="clock-current">
            当前主机时间：20{{ clockForm.year }}-{{ clockForm.month }}-{{ clockForm.day }}
            {{ clockForm.hour }}:{{ clockForm.minute }}
          </p>
          <el-form-item
            v-for="field in clockFields"
            :key="field.key"
            :label="`${field.label} (${field.min}~${field.max})：`"
          >
            <el-input-number
              v-model="clockForm[field.key]"
              :disabled="!editable"
              :min="field.min"
              :max="field.max"
              :controls="false"
            />
          </el-form-item>
          <div v-if="editable" class="settings-actions settings-actions--full">
            <el-button type="primary" @click="saveClock">保存</el-button>
          </div>
        </el-form>

        <el-form v-else :model="filterForm" class="settings-form settings-form--filter">
          <div v-for="field in filterFields" :key="field.key" class="filter-row">
            <strong>{{ field.label }} 流量*10：</strong>
            <el-input-number
              v-model="filterForm[field.flow]"
              :disabled="!editable"
              :min="0"
              :max="65535"
              :step="1"
              :controls="false"
            />
            <span>天数：</span>
            <el-input-number
              v-model="filterForm[field.days]"
              :disabled="!editable"
              :min="0"
              :max="65535"
              :controls="false"
            />
          </div>
          <div v-if="editable" class="settings-actions settings-actions--full">
            <el-button type="primary" @click="saveFilter">保存</el-button>
          </div>
        </el-form>
      </section>
    </div>
  </el-dialog>
</template>

<script>
import {
  createClockSettings,
  createFilterSettings,
  createSystemSettings,
  createTimingSettings,
  parseClockSettings,
  parseFilterSettings,
  parseSystemSettings,
  parseTimingSettings
} from "../../utils/protocol/settings";
import {
  buildClockCommand,
  buildFilterCommand,
  buildSystemCommand,
  buildTimingCommand
} from "../../utils/protocol/commands";

export default {
  name: "DeviceSettingsDialog",
  props: {
    value: {
      type: Boolean,
      default: false
    },
    protocol: {
      type: Object,
      default: () => ({})
    },
    editable: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      activeTab: "system",
      systemForm: createSystemSettings(),
      timingForm: createTimingSettings(),
      clockForm: createClockSettings(),
      filterForm: createFilterSettings(),
      periods: [1, 2, 3],
      tabs: [
        { value: "system", label: "主机系统参数" },
        { value: "timing", label: "杀菌消毒参数" },
        { value: "clock", label: "主机时钟参数" },
        { value: "filter", label: "滤料寿命参数" }
      ],
      timingDevices: [
        { key: "waterReturnValve", label: "回水阀" },
        { key: "device", label: "杀菌装置" }
      ],
      systemFields: [
        { key: "jinShuiYaLiDi", label: "进水压力过低值" },
        { key: "chunShuiXiangZhiShuiTingZhi", label: "纯水箱制水停止值(%)", max: 100 },
        { key: "chunShuiXiangZhiShuiQiDong", label: "纯水箱制水启动值(%)", max: 100 },
        { key: "moChongXiShiChang", label: "冲洗时长(秒)" },
        { key: "moQianYaLiGao", label: "膜前压力过高值" },
        { key: "gongShuiYaLiDi", label: "供水压力过低值" },
        { key: "gongShuiBangQiDong", label: "供水泵启动值" },
        { key: "gongShuiBangTingZhi", label: "供水泵停止值" },
        { key: "chunShuiXiangPaiKong", label: "纯水箱排空间隔(天)" },
        { key: "jinShuiLiuLiangXiShu", label: "进水流量系数" },
        { key: "chunShuiLiuLiangXiShu", label: "纯水流量系数" },
        { key: "gongShuiLiuLiangXiShu", label: "供水流量系数" },
        { key: "zhiShuiChongXiJianGe", label: "累计制水冲洗时间(分钟)" },
        { key: "jinShuiYaLiGao", label: "进水压力过高值" },
        { key: "chunShuiXiangGongShuiQiDong", label: "供水启动水位(%)", max: 100 },
        { key: "chunShuiXiangGongShuiTingZhi", label: "供水停止水位(%)", max: 100 },
        { key: "chunShuiXunHuanJianGe", label: "纯水循环间隔(分钟)" },
        { key: "chunShuiXunHuanShiChang", label: "纯水循环时长(分钟)" }
      ],
      clockFields: [
        { key: "year", label: "年份", min: 0, max: 99 },
        { key: "month", label: "月份", min: 1, max: 12 },
        { key: "day", label: "日期", min: 1, max: 31 },
        { key: "hour", label: "时钟", min: 0, max: 23 },
        { key: "minute", label: "分钟", min: 0, max: 59 }
      ],
      filterFields: [
        { key: "quartzSand", label: "石英砂", flow: "quartzSandFlow", days: "quartzSandDays" },
        { key: "activatedCarbon", label: "活性炭", flow: "activatedCarbonFlow", days: "activatedCarbonDays" },
        { key: "softeningResin", label: "软化树脂", flow: "softeningResinFlow", days: "softeningResinDays" },
        { key: "regenerationSalt", label: "再生盐", flow: "regenerationSaltFlow", days: "regenerationSaltDays" },
        { key: "precisionFilter", label: "精密滤芯", flow: "precisionFilterFlow", days: "precisionFilterDays" },
        { key: "roMembrane", label: "RO/NF膜", flow: "roMembraneFlow", days: "roMembraneDays" }
      ]
    };
  },
  computed: {
    visibleProxy: {
      get() {
        return this.value;
      },
      set(value) {
        this.$emit("input", value);
      }
    }
  },
  watch: {
    protocol() {
      this.loadForms();
    },
    value(visible) {
      if (visible) {
        this.loadForms();
      }
    }
  },
  created() {
    this.loadForms();
  },
  methods: {
    loadForms() {
      this.systemForm = parseSystemSettings(this.protocol);
      this.timingForm = parseTimingSettings(this.protocol);
      this.clockForm = parseClockSettings(this.protocol);
      this.filterForm = parseFilterSettings(this.protocol);
    },
    timingField(device, phase, unit, period) {
      return `${device}${phase}${unit}${period}`;
    },
    getTimingValue(device, phase, unit, period) {
      return this.timingForm[this.timingField(device, phase, unit, period)];
    },
    setTimingValue(device, phase, unit, period, value) {
      this.$set(
        this.timingForm,
        this.timingField(device, phase, unit, period),
        value
      );
    },
    submit(builder, form, successMessage) {
      try {
        const ms = builder(form);
        this.$emit("command", { ms, successMessage });
      } catch (error) {
        this.$message.error(error.message || "参数格式错误");
      }
    },
    saveSystem() {
      this.submit(buildSystemCommand, this.systemForm, "参数设置成功");
    },
    saveTiming() {
      this.submit(buildTimingCommand, this.timingForm, "定时任务设置保存成功");
    },
    saveClock() {
      this.submit(buildClockCommand, this.clockForm, "时间设置保存成功");
    },
    saveFilter() {
      this.submit(buildFilterCommand, this.filterForm, "滤料设置保存成功");
    }
  }
};
</script>

<style lang="scss" scoped>
.device-settings-dialog /deep/ .el-dialog {
  overflow: hidden;
  max-width: calc(100vw - 32px);
  max-height: 92vh;
  margin: 4vh auto !important;
  background: #0e2035 !important;
}

.device-settings-dialog /deep/ .el-dialog__header {
  height: 60px;
  box-sizing: border-box;
  padding: 18px 20px;
  background: linear-gradient(90deg, #3091ff 0%, #023ba3 100%) !important;
}

.device-settings-dialog /deep/ .el-dialog__title {
  font-size: 20px;
  font-weight: 500;
  color: #fff;
}

.device-settings-dialog /deep/ .el-dialog__headerbtn {
  top: 18px;
  right: 20px;
  width: 24px;
  height: 24px;
}

.device-settings-dialog /deep/ .el-dialog__headerbtn .el-dialog__close {
  font-size: 20px;
  color: #fff;
}

.device-settings-dialog /deep/ .el-dialog__body {
  max-height: calc(92vh - 60px);
  overflow: hidden;
  padding: 0 !important;
  background: #0e2035 !important;
}

.settings-layout {
  display: flex;
  height: 440px;
  max-height: calc(92vh - 60px);
  padding: 8px;
  border: 1px solid #2b88f5;
  box-sizing: border-box;
  background: #0e2035;
}

.settings-tabs {
  display: flex;
  width: 140px;
  flex: 0 0 140px;
  flex-direction: column;
  overflow-y: auto;
}

.settings-tabs button {
  width: 140px;
  height: 44px;
  padding: 0;
  border: 0;
  outline: 0;
  background: transparent;
  color: rgba(255, 255, 255, 0.6);
  cursor: pointer;
  font-family: inherit;
  font-size: 15px;
  font-weight: 500;
  line-height: 44px;
  text-align: center;
}

.settings-tabs button.active {
  color: #fff;
  background-image: url("/images/dashboard/dnsl.png");
  background-repeat: no-repeat;
  background-size: 100% 100%;
}

.settings-content {
  display: flex;
  min-width: 0;
  min-height: 0;
  padding: 10px 12px;
  box-sizing: border-box;
  flex: 1 1 auto;
  flex-direction: column;
  overflow: auto;
  background: rgba(48, 145, 255, 0.2);
}

.settings-form,
.timing-settings {
  width: 100%;
}

.settings-form--grid {
  display: grid;
  min-width: 0;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px 12px;
}

.settings-form--grid /deep/ .el-form-item {
  width: auto;
  min-width: 0;
  height: 32px;
  margin: 0 !important;
}

.device-settings-dialog /deep/ .el-form-item__label {
  height: 32px;
  white-space: nowrap;
  font-size: 14px;
  font-weight: 500;
  line-height: 32px;
  color: rgba(255, 255, 255, 0.8);
}

.device-settings-dialog /deep/ .el-form-item {
  margin-bottom: 10px;
}

.device-settings-dialog /deep/ .el-input-number__inner,
.device-settings-dialog /deep/ .el-input__inner {
  height: 32px;
  padding: 0 12px;
  border: 0 !important;
  border-radius: 0;
  background: #0e2035 !important;
  color: rgba(255, 255, 255, 0.8) !important;
}

.device-settings-dialog /deep/ .el-input-number__inner:focus,
.device-settings-dialog /deep/ .el-input__inner:focus {
  background: #1a3a68 !important;
}

.device-settings-dialog /deep/ .el-input.is-disabled .el-input__inner,
.device-settings-dialog /deep/ .el-input-number.is-disabled .el-input__inner {
  background: #0e2035 !important;
  color: rgba(255, 255, 255, 0.8) !important;
}

.settings-form--grid /deep/ .el-form-item__label {
  width: 188px !important;
  padding-right: 10px;
}

.settings-form--grid /deep/ .el-form-item__content {
  min-width: 0;
  height: 32px;
  margin-left: 188px !important;
  line-height: 32px;
}

.settings-form--grid /deep/ .el-input-number.settings-input {
  width: 124px;
  line-height: 32px;
}

.settings-actions {
  display: flex;
  justify-content: center;
  margin-top: auto;
  padding-top: 8px;
  text-align: center;
}

.settings-actions--full {
  width: 100%;
}

.settings-form--grid .settings-actions--full {
  width: auto;
  padding-top: 0;
  grid-column: 1 / -1;
}

.settings-actions /deep/ .el-button {
  width: 88px;
  height: 34px;
  padding: 0;
  border: 0;
  border-radius: 4px;
  background: linear-gradient(180deg, #519efc 0%, #0073fd 100%);
  font-size: 15px;
  font-weight: 500;
  line-height: 34px;
  color: #fff;
}

.timing-settings,
.settings-form--clock {
  display: flex;
  flex-direction: column;
}

.settings-tip {
  display: flex;
  min-height: 20px;
  align-items: flex-start;
  color: #3fafff;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
}

.settings-tip p {
  margin: 0;
  white-space: normal;
}

.settings-tip__icon {
  width: 20px;
  height: 20px;
  margin-right: 4px;
  flex: 0 0 20px;
  background-image: url("/images/dashboard/tipsIcon.png");
  background-repeat: no-repeat;
  background-size: 100% 100%;
}

.timing-days {
  display: grid;
  min-width: 0;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px 12px;
  margin-top: 8px;
}

.timing-days /deep/ .el-form-item {
  min-width: 0;
  margin: 0 !important;
}

.timing-days /deep/ .el-form-item__label {
  width: 204px !important;
  padding-right: 10px;
  white-space: nowrap;
}

.timing-days /deep/ .el-form-item__content {
  margin-left: 204px !important;
}

.timing-days /deep/ .el-input-number {
  width: 92px;
}

.time-period {
  min-width: 0;
  margin-top: 8px;
}

.time-period h4 {
  margin: 0;
  color: #3fafff;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
}

.time-row {
  display: flex;
  height: 32px;
  margin-top: 8px;
  align-items: center;
  color: rgba(255, 255, 255, 0.8);
  font-size: 14px;
  font-weight: 500;
}

.time-row strong {
  width: 56px;
  margin-right: 20px;
  font-weight: 500;
  white-space: nowrap;
  text-align: right;
}

.time-row span {
  margin-right: 5px;
  white-space: nowrap;
}

.time-row i {
  margin: 0 5px;
  font-style: normal;
}

.time-row /deep/ .el-input-number {
  width: 52px;
}

.time-row /deep/ .el-input-number__inner {
  padding: 0 5px;
  text-align: center;
}

.clock-current {
  margin: 8px 0 0;
  color: #3fafff;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
}

.settings-form--clock /deep/ .el-form-item {
  width: 230px;
  margin: 10px 0 0;
}

.settings-form--clock /deep/ .el-input-number {
  width: 100px;
}

.settings-form--filter {
  min-width: 0;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}

.filter-row {
  display: flex;
  width: 100%;
  height: 32px;
  margin-bottom: 10px;
  align-items: center;
  color: rgba(255, 255, 255, 0.8);
  font-size: 14px;
  font-weight: 500;
  line-height: 32px;
}

.filter-row strong {
  width: 220px;
  flex: 0 0 220px;
  font-weight: 500;
  white-space: nowrap;
  text-align: right;
}

.filter-row span {
  margin-left: 32px;
  white-space: nowrap;
}

.filter-row /deep/ .el-input-number {
  width: 128px;
  margin-left: 8px;
}

.filter-row /deep/ .el-input-number__inner {
  text-align: center;
}

</style>
